const RECRUITMENT_AUTH_CLIENT_ID = 'recruitment-app';

type TokenPayload = {
    realm_access?: { roles?: unknown };
    resource_access?: Record<string, { roles?: unknown } | undefined>;
};

interface OidcProfileIdentity {
    sub?: string;
    preferred_username?: string;
    email?: string;
    name?: string;
}

export interface OidcAuthenticatedUser {
    access_token?: string;
    refresh_token?: string;
    profile: OidcProfileIdentity;
}

export function extractRolesFromToken(token: string | null | undefined, configuredClientId: string): string[] {
    if (!token || typeof token !== 'string') return [];

    try {
        const [, encodedPayload] = token.split('.');
        if (!encodedPayload) return [];

        const payload = JSON.parse(decodeBase64Url(encodedPayload)) as TokenPayload;
        const configuredClientRoles = payload.resource_access?.[configuredClientId]?.roles;
        const recruitmentRoles = payload.resource_access?.[RECRUITMENT_AUTH_CLIENT_ID]?.roles;

        return Array.from(new Set([
            ...stringRoles(payload.realm_access?.roles),
            ...stringRoles(configuredClientRoles),
            ...stringRoles(recruitmentRoles),
        ]));
    } catch {
        return [];
    }
}

export function buildAuthorizedAuthHydrationForClient(
    token: string | null | undefined,
    profile: OidcProfileIdentity,
    configuredClientId: string,
) {
    const roles = extractRolesFromToken(token, configuredClientId);
    return {
        role: roles[0] ?? 'user',
        user: {
            id: profile.sub ?? '',
            username: profile.preferred_username ?? profile.email ?? '',
            email: profile.email ?? '',
            name: profile.name ?? '',
            roles,
        },
    };
}

export function buildAuthorizedSessionForClient(
    user: OidcAuthenticatedUser,
    configuredClientId: string,
) {
    return {
        accessToken: user.access_token ?? '',
        refreshToken: user.refresh_token ?? '',
        ...buildAuthorizedAuthHydrationForClient(
            user.access_token,
            user.profile,
            configuredClientId,
        ),
    };
}

export function bindAuthorizedSessionRefreshForClient(
    subscribe: (listener: (user: OidcAuthenticatedUser) => void) => () => void,
    replaceSession: (session: ReturnType<typeof buildAuthorizedSessionForClient>) => void,
    configuredClientId: string,
    afterSessionReplaced?: () => void,
): () => void {
    return subscribe((user) => {
        replaceSession(buildAuthorizedSessionForClient(user, configuredClientId));
        afterSessionReplaced?.();
    });
}

function stringRoles(value: unknown): string[] {
    return Array.isArray(value) ? value.filter((role): role is string => typeof role === 'string') : [];
}

function decodeBase64Url(value: string): string {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
    return atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='));
}
